import { createFileRoute } from '@tanstack/react-router';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

export const Route = createFileRoute('/_site/faq')({
  head: () => ({
    meta: [
      { title: 'FAQ — dice, death and the AI game master | Everwake' },
      {
        name: 'description',
        content:
          'Answers about Everwake: can you die, is the game master fair, can you play by text, why 18+, what a session costs, and what the Guia is.',
      },
      { property: 'og:title', content: 'Everwake FAQ' },
      {
        property: 'og:description',
        content:
          'Can you die? Is the game master fair? Can you play by text? Straight answers about Everwake.',
      },
      { property: 'og:type', content: 'article' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
  }),
  component: Faq,
});

const faqs = [
  {
    id: 'rules',
    q: 'What rules does Everwake use?',
    a:
      'Everwake is its own 5E-compatible game, built on the SRD 5.2.1 under CC-BY-4.0. If you know how a d20 check works, you already know how to sit down. Its world, rules engine and content are original to Everwake.',
  },
  {
    id: 'die',
    q: 'Can I die?',
    a:
      'Yes. Death saves run exactly as the rules write them and every outcome is transparent and final. A clear warning gives you agency before an unchosen deadly beat. If your character dies, the campaign, the journal and the Codex continue without them.',
  },
  {
    id: 'fair',
    q: 'Is the game master fair?',
    a:
      'The game is verifiable by design. Before play the server commits to a secret seed and publishes its hash. Every roll is derived from that seed and its sequence number. At the end of the session the seed is revealed and your browser can recompute every die that was thrown.',
  },
  {
    id: 'text',
    q: 'Can I play by text?',
    a:
      'Yes. Voice is push-to-talk by default and captions are always on, but the typing box is always there and works for everything. Voice is a per-session setting.',
  },
  {
    id: 'group',
    q: 'Do I need a group?',
    a: 'No. Everwake is built for one player and one game master, so you play exactly when you want, for as long as you want.',
  },
  {
    id: '18',
    q: 'Why 18+?',
    a:
      'The fiction is written for adults, with violence, consequences and subject matter crafted to preserve the gravity of every choice. Before each session, you draw your lines and the story is shaped inside the boundaries you choose.',
  },
  {
    id: 'world',
    q: 'Does the world change while I am away?',
    a:
      'The world waits for your return. It is a seeded simulation that runs only while you play, and your next session opens at the exact moment the last one ended — the same place, the same hour, mid-breath. Time passes because you spend it: a walk, a night in camp, a road taken.',
  },
  {
    id: 'cost',
    q: 'What does a session cost?',
    a:
      'One session pass is ten credits and opens about two hours of play: a complete arc with room for conversation, discovery and consequence. The credit price and free-trial length will be announced at launch.',
  },
  {
    id: 'guia',
    q: 'What is the Guia?',
    a:
      'A free out-of-character assistant (say "GHEE-ah") that explains rules, your character sheet and the dice log. It operates entirely outside the game flow, preserving your credits, the mystery of the world, and the surprise of the dice.',
  },
  {
    id: 'gm',
    q: 'Who is the game master?',
    a:
      'Your game master is an AI system, and the provider and model in use are shown inside every session. A deterministic engine owns the rules, the world and the dice; the AI receives each validated outcome and brings it to life.',
  },
];

function Faq() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16">
      <p className="rule-line">FAQ</p>
      <h1 className="mt-4 font-display text-4xl md:text-5xl">Straight answers</h1>

      <Accordion type="single" collapsible className="mt-10">
        {faqs.map((f) => (
          <AccordionItem key={f.id} value={f.id}>
            <AccordionTrigger className="text-left font-display text-lg">{f.q}</AccordionTrigger>
            <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
